import { useState } from 'react';
import type { ReactNode } from 'react';
import { useApi, useRead } from '../../context';
import { Empty, Failure, Loading } from '../../bits';
import { Menu, Panel, TabPanel, Tabs } from '../../kit';
import { useT } from '../../../messages';
import type { TenantCatalog } from '../../../wire';
import type { ApiFailure } from '../../../api/errors';
import { ItemList } from './ItemList';
import { ItemEditor } from './ItemEditor';
import { VerticalDetail } from './VerticalDetail';
import { AddItem, RenameHeading, SeedFromFile } from './Tools';
import { KINDS, isReadOnlyKind, kindLabel, kindOf } from './text';
import type { Kind } from './text';
import './catalog.css';

/**
 * **What they sell** — a list on the left, one editor on the right
 * (docs/design/console-redesign.html, the catalog panel).
 *
 * It used to be every item drawn as a full inline form, one under another, so
 * a spa with twenty services was a page of two hundred boxes and the one being
 * edited was wherever the scroll happened to be. Now the list says what is
 * sold — grouped by heading, with lengths, price and the flags worth seeing —
 * and choosing a row opens that one thing beside it.
 *
 * **The choice is the address's** (`#/b/<id>/catalog?item=<item id>`), so a
 * refresh keeps the open item and a link can point at one treatment. Rendered
 * on its own, as every test renders it, the tab keeps its own choice.
 *
 * **Kind tabs only for kinds that exist**: a spa sees its services and nothing
 * else; an island inn sees Rooms and Sailings. A room type or a trip opens a
 * read-only sheet — the catalog file is its door.
 *
 * The things done to the catalog as a whole — rename a heading, add one thing,
 * seed from a file — open in the right half from the list's header, in place of
 * the editor. When the Website tab hands over the owner's menu (`seedFrom`),
 * the seeder is what opens, with the file in it; nothing is written until an
 * operator presses Dry run and then Seed this.
 */
export function CatalogSection({
  tenantId,
  seedFrom = null,
  currency = 'USD',
  item: routedItem,
  onItem,
}: {
  tenantId: string;
  /** The owner's menu, handed from the Website tab. Client state; nothing written. */
  seedFrom?: string | null | undefined;
  /** The business's currency, for the prices the list prints. */
  currency?: string | undefined;
  /** The open item the address names; omitted, the tab keeps its own. */
  item?: string | undefined;
  onItem?: ((item: string | undefined) => void) | undefined;
}): ReactNode {
  const api = useApi();
  const t = useT();
  const read = useRead(() => api.catalog(tenantId), [tenantId]);

  if (read.value === null) {
    return (
      <>
        {read.failure !== null ? <Failure failure={read.failure} /> : null}
        {read.loading ? <Loading what={t('catalog.list.what')} /> : null}
      </>
    );
  }
  if (!read.value.editable) {
    /*
     * A deployment one migration behind its console. The server answers this by
     * probing its own privileges rather than by guessing, so the tab says what
     * is true instead of drawing fields that all refuse.
     */
    return <Empty>{read.value.note ?? t('catalog.notGranted')}</Empty>;
  }
  return (
    <CatalogBody
      tenantId={tenantId}
      catalog={read.value}
      failure={read.failure}
      seedFrom={seedFrom}
      currency={currency}
      routedItem={routedItem}
      onItem={onItem}
      onChanged={read.reload}
    />
  );
}

type Mode = 'item' | 'add' | 'rename' | 'seed';

function CatalogBody({
  tenantId,
  catalog,
  failure,
  seedFrom,
  currency,
  routedItem,
  onItem,
  onChanged,
}: {
  tenantId: string;
  catalog: TenantCatalog;
  failure: ApiFailure | null;
  seedFrom: string | null;
  currency: string;
  routedItem: string | undefined;
  onItem: ((item: string | undefined) => void) | undefined;
  onChanged: () => void;
}): ReactNode {
  const t = useT();
  const [mode, setMode] = useState<Mode>(seedFrom === null ? 'item' : 'seed');
  const [ownItem, setOwnItem] = useState<string | undefined>(undefined);
  const [pickedKind, setPickedKind] = useState<Kind | null>(null);

  const items = catalog.items;
  const locale = catalog.default_locale;
  const chosen = routedItem ?? ownItem;
  const present = KINDS.filter((k) => items.some((i) => kindOf(i.kind) === k));
  const chosenItem = items.find((i) => i.item_id === chosen);

  // The open item decides the kind tab, so a link to a room opens Rooms.
  const activeKind: Kind | undefined =
    chosenItem !== undefined
      ? kindOf(chosenItem.kind)
      : pickedKind !== null && present.includes(pickedKind)
        ? pickedKind
        : present[0];
  const shown = items.filter((i) => kindOf(i.kind) === activeKind);
  // Nothing chosen yet (or a stale id): the first of the kind is open, without
  // writing it to the address — an address only changes when somebody chooses.
  const open = chosenItem ?? shown[0];

  const select = (itemId: string): void => {
    setOwnItem(itemId);
    onItem?.(itemId);
    setMode('item');
  };

  const pickKind = (kind: Kind): void => {
    setPickedKind(kind);
    const first = items.find((i) => kindOf(i.kind) === kind);
    if (first !== undefined) select(first.item_id);
  };

  const list =
    items.length === 0 ? (
      <p className="muted catalog-list__empty">{t('catalog.list.empty')}</p>
    ) : (
      <ItemList
        items={shown}
        locale={locale}
        currency={currency}
        selected={mode === 'item' ? (open?.item_id ?? null) : null}
        onSelect={select}
      />
    );

  const detail = ((): ReactNode => {
    if (mode === 'add' && catalog.commerce_editable) {
      return (
        <AddItem
          tenantId={tenantId}
          onClose={() => setMode('item')}
          onCreated={(itemId) => {
            onChanged();
            select(itemId);
          }}
        />
      );
    }
    if (mode === 'rename') {
      return (
        <RenameHeading
          tenantId={tenantId}
          catalog={catalog}
          onChanged={onChanged}
          onClose={() => setMode('item')}
        />
      );
    }
    if (mode === 'seed' || open === undefined) {
      return (
        <SeedFromFile
          // Keyed on the handed document, so "Seed this" on the Website tab
          // refills the box rather than being swallowed by an initial value
          // that only ever runs once.
          key={seedFrom ?? 'empty'}
          tenantId={tenantId}
          commerceEditable={catalog.commerce_editable}
          initial={seedFrom ?? undefined}
          onChanged={onChanged}
          onClose={() => setMode('item')}
        />
      );
    }
    if (isReadOnlyKind(kindOf(open.kind))) {
      return (
        <VerticalDetail
          key={open.item_id}
          item={open}
          locale={locale}
          currency={currency}
          satellites={catalog.vertical_satellites}
        />
      );
    }
    return (
      <ItemEditor
        // A different item is a different form: no draft survives the switch.
        key={open.item_id}
        tenantId={tenantId}
        item={open}
        locale={locale}
        currency={currency}
        commerceEditable={catalog.commerce_editable}
        houseBuffers={catalog.house_buffers}
        uploadsConfigured={catalog.photo_uploads_configured}
        /*
         * What could be attached to this one: every other scheduled service,
         * retired ones included, because an add-on is an ordinary item and an
         * attach-only treatment is *supposed* to look odd standing alone.
         * Computed from the catalog already in hand — no second read to go
         * stale against the one the tab is drawn from.
         */
        siblings={items.filter((i) => i.kind === 'scheduled' && i.item_id !== open.item_id)}
        onChanged={onChanged}
      />
    );
  })();

  const title =
    activeKind === undefined
      ? t('catalog.list.nothing')
      : t('catalog.list.title', { kind: kindLabel(t, activeKind), count: shown.length });

  return (
    <div className="catalog">
      {failure !== null ? <Failure failure={failure} /> : null}
      <div className="catalog__split">
        <Panel
          className="catalog__list"
          title={title}
          actions={
            <>
              {catalog.commerce_editable ? (
                <button
                  type="button"
                  className="button button--sm"
                  aria-pressed={mode === 'add'}
                  onClick={() => setMode('add')}
                >
                  {t('catalog.list.add')}
                </button>
              ) : null}
              <Menu
                label={t('catalog.list.more')}
                items={[
                  {
                    key: 'rename',
                    label: t('catalog.categories'),
                    hint: t('catalog.tools.renameHint'),
                    onPick: () => setMode('rename'),
                  },
                  {
                    key: 'seed',
                    label: t('catalog.seed'),
                    hint: catalog.commerce_editable
                      ? t('catalog.tools.seedHint')
                      : t('catalog.tools.seedHintLive'),
                    onPick: () => setMode('seed'),
                  },
                ]}
              />
            </>
          }
        >
          {present.length > 1 && activeKind !== undefined ? (
            <>
              <Tabs
                label={t('catalog.list.kinds')}
                idPrefix="catalog-kind"
                tabs={present.map((k) => ({ id: k, label: kindLabel(t, k) }))}
                current={activeKind}
                onPick={pickKind}
              />
              <TabPanel idPrefix="catalog-kind" id={activeKind}>
                {list}
              </TabPanel>
            </>
          ) : (
            list
          )}
        </Panel>
        <div className="catalog__detail">{detail}</div>
      </div>
    </div>
  );
}
